import { axiosRubyClient } from '@/util/axios/axiosClient';

import {
  DashboardFilterValues,
  EMPTY_FILTERS,
  EMPTY_QUEUE_FILTERS,
  FillStatus,
  QueueFilterValues,
  QueueName,
} from './types';

// Persistence for everything the admin CHOSE on Dashboard V2 (GRAVY-10856).
//
// V2 held its filters in `useState` on the page, so they survived a tab switch
// and nothing else: opening a shift and pressing Back, a refresh, or a new
// session all dropped them and the admin rebuilt the filter by hand. Dashboard
// V1 has saved them since it shipped — in the URL, so Back/refresh/shared links
// work, and on the server, so they survive logout — and this is the same two
// mechanisms for V2.
//
// ONE representation serves both. The URL and the stored blob carry identical
// bytes, so there is a single encoder, a single decoder, and no way for the two
// to drift into disagreeing about what "filtered by Client A" means.
//
// ## Why the keys are short
//
// This travels in a query string that also carries `?tab=`, `?sd=` and
// `?view=`, and a client selection can run to a dozen ids. Short keys keep a
// shared link a link rather than a paragraph — the same reason V1 has
// `filtersKeysMapper`.
//
// ## Why decoding is defensive rather than schema-parsed
//
// The input is a blob the client itself wrote, possibly by an older or newer
// build of the app, possibly hand-edited in the address bar. A strict parse
// would reject exactly the cross-version case the store exists to survive, and
// the failure mode — a dashboard that will not load — is far worse than the one
// it prevents. So every field is coerced independently and anything
// unrecognised is dropped.

export const SAVED_FILTER_TYPE = 'dashboard_v2';

const SAVED_FILTERS_PATH = `/api/admin/user_filters/${SAVED_FILTER_TYPE}`;

/** Payload format. Bumped only if an old blob must be rejected outright. */
const FORMAT_VERSION = 1;

export const QUEUE_NAMES: QueueName[] = [
  'no_show',
  'missing_timesheet',
  'needs_approval',
];

/** Everything on the page the admin chose and expects to find again. */
export type DashboardV2ViewState = {
  calendar: DashboardFilterValues;
  /**
   * Whether the Calendar filter bar is revealed. Persisted with the selections
   * for the reason the page already gives for keeping it across tab switches:
   * an admin who comes back to applied filters should be able to SEE what is
   * applied without opening the bar again.
   */
  calendarFiltersOpen: boolean;
  queues: Record<QueueName, QueueFilterValues>;
  /** 1-based, per queue. */
  queuePages: Record<QueueName, number>;
};

export const DEFAULT_VIEW_STATE: DashboardV2ViewState = {
  calendar: EMPTY_FILTERS,
  calendarFiltersOpen: false,
  queues: {
    no_show: EMPTY_QUEUE_FILTERS,
    missing_timesheet: EMPTY_QUEUE_FILTERS,
    needs_approval: EMPTY_QUEUE_FILTERS,
  },
  queuePages: { no_show: 1, missing_timesheet: 1, needs_approval: 1 },
};

// Short queue keys, so the blob names a queue in two characters rather than
// seventeen.
const QUEUE_KEYS: Record<QueueName, string> = {
  no_show: 'ns',
  missing_timesheet: 'mt',
  needs_approval: 'na',
};

// ── Encoding ──────────────────────────────────────────────────────────────
//
// Empty dimensions are OMITTED rather than written as `[]`. That keeps the
// blob proportional to what was actually selected, and it is what makes
// "nothing is selected" encode to nothing at all — which is how the caller
// knows to clear the saved filter instead of storing an empty one.

const putIds = (
  target: Record<string, unknown>,
  key: string,
  ids: number[],
) => {
  if (ids.length) target[key] = ids;
};

const putStrings = (
  target: Record<string, unknown>,
  key: string,
  values: string[],
) => {
  if (values.length) target[key] = values;
};

const encodeCalendar = (v: DashboardFilterValues): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  putIds(out, 'cu', v.customerIds);
  putIds(out, 'ac', v.accountIds);
  putIds(out, 'sk', v.skillIds);
  putIds(out, 'rg', v.regionIds);
  putStrings(out, 'st', v.status);
  putStrings(out, 'jt', v.jobType);
  putStrings(out, 'fs', v.fillStatus);
  putStrings(out, 'fr', v.fillReason ?? []);
  return out;
};

const encodeQueue = (
  v: QueueFilterValues,
  page: number,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  putIds(out, 'cu', v.customerIds);
  putIds(out, 'ac', v.accountIds);
  putIds(out, 'sk', v.skillIds);
  putIds(out, 'rg', v.regionIds);
  if (v.startDate) out.sd = v.startDate;
  if (v.endDate) out.ed = v.endDate;
  // Page 1 is the default, so only a page the admin actually paged to is
  // written. A saved `p: 1` would be noise in every blob.
  if (page > 1) out.p = page;
  return out;
};

/**
 * Serializes the page's view state, or returns `null` when it is entirely
 * default.
 *
 * `null` is the signal that there is nothing to remember — the caller drops the
 * URL param and DELETEs the saved filter, which is what makes "Reset filters"
 * stick across a reload rather than being undone by the next hydration.
 */
export const encodeViewState = (state: DashboardV2ViewState): string | null => {
  const calendar = encodeCalendar(state.calendar);

  const queues: Record<string, unknown> = {};
  QUEUE_NAMES.forEach((queue) => {
    const encoded = encodeQueue(state.queues[queue], state.queuePages[queue]);
    if (Object.keys(encoded).length) queues[QUEUE_KEYS[queue]] = encoded;
  });

  const payload: Record<string, unknown> = { v: FORMAT_VERSION };
  if (Object.keys(calendar).length) payload.c = calendar;
  // Only worth recording when open — closed is the default the page starts in.
  if (state.calendarFiltersOpen) payload.o = 1;
  if (Object.keys(queues).length) payload.q = queues;

  // `v` alone means nothing was selected anywhere.
  if (Object.keys(payload).length === 1) return null;

  return JSON.stringify(payload);
};

// ── Decoding ──────────────────────────────────────────────────────────────

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

// Ids arrive as numbers from our own encoder, but a hand-edited URL or an older
// build can deliver strings. Coerced and filtered rather than trusted, so one
// bad entry narrows the filter by one id instead of sending `NaN` to the API.
//
// The type is checked BEFORE coercing, because `Number` is generous in exactly
// the wrong way here: `Number(null)`, `Number('')` and `Number(false)` are all
// 0, so a null in the list would otherwise arrive at the API as a filter on
// customer 0 — a real request for a record that does not exist, returning an
// empty calendar rather than the one id that was readable.
const numberList = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (entry) =>
        (typeof entry === 'number' || typeof entry === 'string') &&
        entry !== '',
    )
    .map((entry) => Number(entry))
    .filter((n) => Number.isFinite(n));
};

const stringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
};

const dateOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null;

const pageNumber = (value: unknown): number => {
  const n = Number(value);
  // Page 0 or a negative page would ask the API for a window that does not
  // exist; anything unparseable means "the admin never paged".
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
};

const decodeCalendar = (value: unknown): DashboardFilterValues => {
  const raw = asRecord(value);
  return {
    customerIds: numberList(raw.cu),
    accountIds: numberList(raw.ac),
    skillIds: numberList(raw.sk),
    regionIds: numberList(raw.rg),
    status: stringList(raw.st),
    jobType: stringList(raw.jt),
    // The band vocabulary is the server's, and an unknown band simply matches
    // nothing — so this is not narrowed further here.
    fillStatus: stringList(raw.fs) as FillStatus[],
    // Smart Fill reason codes; an unknown one matches nothing server-side.
    fillReason: stringList(raw.fr),
  };
};

const decodeQueue = (value: unknown): QueueFilterValues => {
  const raw = asRecord(value);
  return {
    customerIds: numberList(raw.cu),
    accountIds: numberList(raw.ac),
    skillIds: numberList(raw.sk),
    regionIds: numberList(raw.rg),
    startDate: dateOrNull(raw.sd),
    endDate: dateOrNull(raw.ed),
  };
};

/**
 * Rebuilds view state from a stored blob or a URL param.
 *
 * Returns `null` when there is nothing usable — unparseable, the wrong format
 * version, or a payload that decodes to the default state. Callers treat `null`
 * as "this source had no opinion", so an unreadable blob falls through to the
 * next source rather than blanking the filters the previous one restored.
 */
export const decodeViewState = (
  input: unknown,
): DashboardV2ViewState | null => {
  let raw: unknown = input;

  if (typeof input === 'string') {
    if (!input) return null;
    try {
      raw = JSON.parse(input);
    } catch {
      return null;
    }
  }

  const payload = asRecord(raw);
  if (Object.keys(payload).length === 0) return null;
  // An unrecognised version is not decoded on a guess: the shape is not what
  // this build understands, and restoring half of it would apply a filter the
  // admin cannot see in the bar.
  if (Number(payload.v) !== FORMAT_VERSION) return null;

  const queuesRaw = asRecord(payload.q);

  const state: DashboardV2ViewState = {
    calendar: decodeCalendar(payload.c),
    calendarFiltersOpen: Boolean(payload.o),
    queues: {
      no_show: decodeQueue(queuesRaw[QUEUE_KEYS.no_show]),
      missing_timesheet: decodeQueue(queuesRaw[QUEUE_KEYS.missing_timesheet]),
      needs_approval: decodeQueue(queuesRaw[QUEUE_KEYS.needs_approval]),
    },
    queuePages: {
      no_show: pageNumber(asRecord(queuesRaw[QUEUE_KEYS.no_show]).p),
      missing_timesheet: pageNumber(
        asRecord(queuesRaw[QUEUE_KEYS.missing_timesheet]).p,
      ),
      needs_approval: pageNumber(
        asRecord(queuesRaw[QUEUE_KEYS.needs_approval]).p,
      ),
    },
  };

  // Round-tripping to nothing means the payload carried no actual selection —
  // report that as "no opinion" so it never masks a source that has one.
  return encodeViewState(state) === null ? null : state;
};

/**
 * Removes the Client selection a Client Admin does not control.
 *
 * Their customer is fixed by authorization, so `customerIds` is not theirs to
 * choose — the bar shows them Billing Account instead. A saved or shared
 * `customerIds` must never reach them, in either direction: not restored from
 * storage, and not written into it. Their `accountIds` IS user-controlled and
 * is kept, exactly as V1 keeps its `accounts`.
 */
export const stripCustomerLock = (
  state: DashboardV2ViewState,
): DashboardV2ViewState => ({
  ...state,
  calendar: { ...state.calendar, customerIds: [] },
  queues: {
    no_show: { ...state.queues.no_show, customerIds: [] },
    missing_timesheet: { ...state.queues.missing_timesheet, customerIds: [] },
    needs_approval: { ...state.queues.needs_approval, customerIds: [] },
  },
});

/** True when nothing in `state` is a Client selection. */
export const hasCustomerSelection = (state: DashboardV2ViewState): boolean =>
  state.calendar.customerIds.length > 0 ||
  QUEUE_NAMES.some((queue) => state.queues[queue].customerIds.length > 0);

// ── Server ────────────────────────────────────────────────────────────────
//
// REST, parallel to the GraphQL `userFilter` query/mutation V1 uses
// (API::Admin::UserFiltersController). Dashboard V2 speaks REST only.

export const savedFiltersClient = {
  /**
   * The stored blob, or `null` when the admin has never saved one.
   *
   * A failed request is left to throw: "nothing saved" and "the request failed"
   * lead to different behaviour, and only the first should clear what is on
   * screen.
   */
  async get(): Promise<string | null> {
    const { data } = await axiosRubyClient.get(SAVED_FILTERS_PATH);
    const value = data?.data?.filter_value;
    return value ? JSON.stringify(value) : null;
  },

  /** `payload` is the string from `encodeViewState`. */
  async save(payload: string): Promise<void> {
    await axiosRubyClient.put(SAVED_FILTERS_PATH, { filter_value: payload });
  },

  /** What Reset filters reaches, so a reset is not undone by the next load. */
  async clear(): Promise<void> {
    await axiosRubyClient.delete(SAVED_FILTERS_PATH);
  },
};
